import { createColumnHelper, type CellContext } from '@tanstack/react-table';
import { MoreHorizontalIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import type { AccountDTO } from '@invenco/common-interface/accounts';
import { ServiceAgreementAction } from '@invenco/common-interface/accounts';

import { Button } from '@/components/core/button';
import { Card, CardAction, CardHeader } from '@/components/core/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/core/dropdown-menu';
import {
  DataTable,
  DataTableCardBody,
  DataTableCardTitle,
  useDataTable,
  useDataTableState,
} from '@/components/data-table';
import { FormDialog } from '@/components/forms/form-dialog';
import { StatusBadge } from '@/components/status-badge';
import { useGateways } from '@/gateways/GatewayProvider';
import { formatDate } from '@/shared/helpers';
import { Result } from '@/shared/helpers/Result';
import { useGatewayMutation } from '@/shared/hooks/queries';
import { useMessages } from '@/shared/providers/MessagesProvider';
import { serviceAgreementStatusType } from '@/shared/status-maps';
import { serviceAgreementStatusTitle } from '@/shared/title-maps';

import { ServiceAgreementDialogContent } from './service-agreement-dialog-content';

type Props = {
  account: Partial<AccountDTO>;
  isLoading?: boolean;
  isNew?: boolean;
};

type ServiceAgreementRow = NonNullable<AccountDTO['serviceAgreements']>[number];

const columnHelper = createColumnHelper<ServiceAgreementRow>();

type DialogState = {
  isOpen: boolean;
  mode: 'add' | 'edit' | 'terminate';
  selected?: ServiceAgreementRow;
};

function ServiceAgreementActions({
  serviceAgreement,
  onEdit,
  onTerminate,
}: {
  serviceAgreement: ServiceAgreementRow;
  onEdit: (agreement: ServiceAgreementRow) => void;
  onTerminate: (agreement: ServiceAgreementRow) => void;
}) {
  const canEdit = serviceAgreement.allowableActions?.includes(ServiceAgreementAction.EDIT);
  const canTerminate = serviceAgreement.allowableActions?.includes(
    ServiceAgreementAction.TERMINATE,
  );

  if (!canEdit && !canTerminate) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label="Open service agreement actions">
          <MoreHorizontalIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {canEdit ? (
          <DropdownMenuItem onSelect={() => onEdit(serviceAgreement)}>Edit</DropdownMenuItem>
        ) : null}
        {canTerminate ? (
          <DropdownMenuItem variant="destructive" onSelect={() => onTerminate(serviceAgreement)}>
            Terminate
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function renderServiceAgreementStatusCell({
  getValue,
}: CellContext<ServiceAgreementRow, ServiceAgreementRow['status']>) {
  const status = getValue();
  return status ? (
    <StatusBadge
      status={status}
      type={serviceAgreementStatusType}
      title={serviceAgreementStatusTitle}
    />
  ) : null;
}

const createServiceAgreementActionsCell = (
  onEdit: (agreement: ServiceAgreementRow) => void,
  onTerminate: (agreement: ServiceAgreementRow) => void,
) =>
  function ServiceAgreementActionsCell({ row }: CellContext<ServiceAgreementRow, unknown>) {
    return (
      <ServiceAgreementActions
        serviceAgreement={row.original}
        onEdit={onEdit}
        onTerminate={onTerminate}
      />
    );
  };

export function ServiceAgreementsCard({ account, isLoading, isNew }: Props) {
  const { accountsGateway } = useGateways();
  const messages = useMessages();
  const accountId = account.id;
  const serviceAgreements = account.serviceAgreements ?? [];
  const [dialogState, setDialogState] = useState<DialogState>({
    isOpen: false,
    mode: 'add',
  });

  const handleEdit = useCallback(
    (agreement: ServiceAgreementRow) => {
      setDialogState({
        isOpen: true,
        mode: 'edit',
        selected: agreement,
      });
    },
    [setDialogState],
  );

  const handleTerminate = useCallback(
    (agreement: ServiceAgreementRow) => {
      setDialogState({
        isOpen: true,
        mode: 'terminate',
        selected: agreement,
      });
    },
    [setDialogState],
  );

  const { mutate: addServiceAgreement } = useGatewayMutation({
    mutationFn: (val: { locationId: string; commencementDate: string }) =>
      accountsGateway.createServiceAgreement({ ...val, accountId: accountId! }),
    onSuccess: (serviceAgreement) => {
      messages.success('Service agreement created', {
        name: serviceAgreement.name,
      });
    },
    linkedQuery: ['accounts', accountId],
    linkResponseToQuery: false,
  });

  const { mutate: updateServiceAgreement } = useGatewayMutation({
    mutationFn: ({ id, commencementDate }: { id: string; commencementDate: string }) =>
      accountsGateway.setServiceAgreementCommencement(id, { commencementDate }),
    onSuccess: (serviceAgreement) => {
      messages.success('Service agreement updated', {
        name: serviceAgreement.name,
      });
    },
    linkedQuery: ['accounts', accountId],
    linkResponseToQuery: false,
  });

  const { mutate: terminateServiceAgreement } = useGatewayMutation({
    mutationFn: ({ id, terminationDate }: { id: string; terminationDate: string }) =>
      accountsGateway.terminateServiceAgreement(id, { terminationDate }),
    onSuccess: (serviceAgreement) => {
      messages.success('Service agreement terminated', {
        name: serviceAgreement.name,
      });
    },
    linkedQuery: ['accounts', accountId],
    linkResponseToQuery: false,
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
      }),
      columnHelper.accessor('commencementDate', {
        header: 'Commencement date',
        cell: ({ getValue }) => {
          const value = getValue();
          return value ? formatDate(value) : null;
        },
      }),
      columnHelper.accessor('terminationDate', {
        header: 'Termination date',
        cell: ({ getValue }) => {
          const value = getValue();
          return value ? formatDate(value) : null;
        },
      }),
      columnHelper.accessor('locationName', {
        header: 'Location',
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: renderServiceAgreementStatusCell,
      }),
      columnHelper.display({
        id: 'actions',
        cell: createServiceAgreementActionsCell(handleEdit, handleTerminate),
        meta: { align: 'right' },
      }),
    ],
    [handleEdit, handleTerminate],
  );

  const { state, onSortingChange } = useDataTableState({
    columns,
    tableId: 'account-service-agreements',
  });

  const table = useDataTable({
    data: serviceAgreements,
    columns,
    isLoading,
    manualPagination: true,
    manualSorting: false,
    state,
    onSortingChange,
  });

  return (
    <>
      <Card aria-label="Service agreements">
        <CardHeader>
          <DataTableCardTitle table={table}>Service agreements</DataTableCardTitle>
          <CardAction>
            <Button
              size="sm"
              variant="outline"
              disabled={isNew || !accountId}
              onClick={() => setDialogState({ isOpen: true, mode: 'add' })}
            >
              Add service agreement
            </Button>
          </CardAction>
        </CardHeader>
        <DataTableCardBody table={table}>
          <DataTable table={table} />
        </DataTableCardBody>
      </Card>
      <FormDialog
        open={dialogState.isOpen}
        onOpenChange={(isOpen) =>
          setDialogState((prev) => ({
            ...prev,
            isOpen,
            ...(isOpen ? {} : { mode: 'add', selected: undefined }),
          }))
        }
      >
        <ServiceAgreementDialogContent
          accountId={accountId ?? ''}
          mode={dialogState.mode}
          serviceAgreement={dialogState.selected}
          onAdd={async (data) => {
            if (!accountId) return Result.fail('Account ID missing');
            return addServiceAgreement(data);
          }}
          onUpdate={async (data) => {
            if (!data.id) return Result.fail('Service agreement ID missing');
            return updateServiceAgreement(data);
          }}
          onTerminate={async (data) => {
            if (!data.id) return Result.fail('Service agreement ID missing');
            return terminateServiceAgreement(data);
          }}
        />
      </FormDialog>
    </>
  );
}
